import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, Phone, Clock, UserPlus, CheckCircle2, XCircle, Trash2, BarChart3, Lock, Unlock, Pencil, Search, LifeBuoy, Paperclip, Building2, Eye, EyeOff, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { formatDoctorName } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = [
  { id: 'general', name_fr: 'Médecine générale', name_ar: 'الطب العام' },
  { id: 'cardiology', name_fr: 'Cardiologie', name_ar: 'أمراض القلب' },
  { id: 'dermatology', name_fr: 'Dermatologie', name_ar: 'الأمراض الجلدية' },
  { id: 'gynecology', name_fr: 'Gynécologie', name_ar: 'أمراض النساء' },
  { id: 'ophthalmology', name_fr: 'Ophtalmologie', name_ar: 'طب العيون' },
  { id: 'pediatrics', name_fr: 'Pédiatrie', name_ar: 'طب الأطفال' },
  { id: 'dentistry', name_fr: 'Dentiste', name_ar: 'طب الأسنان' },
  { id: 'orthopedics', name_fr: 'Orthopédie', name_ar: 'جراحة العظام' },
  { id: 'psychiatry', name_fr: 'Psychiatrie', name_ar: 'الطب النفسي' },
  { id: 'neurology', name_fr: 'Neurologie', name_ar: 'أمراض الأعصاب' },
  { id: 'urology', name_fr: 'Urologie', name_ar: 'المسالك البولية' },
  { id: 'ent', name_fr: 'ORL', name_ar: 'أنف أذن حنجرة' },
  { id: 'radiology', name_fr: 'Radiologie', name_ar: 'الأشعة' },
  { id: 'gastroenterology', name_fr: 'Gastro-entérologie', name_ar: 'أمراض الجهاز الهضمي' },
  { id: 'pneumology', name_fr: 'Pneumologie', name_ar: 'أمراض الصدر' },
  { id: 'general_surgery', name_fr: 'Chirurgie générale', name_ar: 'جراحة عامة' },
  { id: 'endocrinology', name_fr: 'Endocrinologie', name_ar: 'علم الغدد الصماء' },
  { id: 'anesthesiology', name_fr: 'Anesthésiologie', name_ar: 'التخدير' },
  { id: 'rheumatology', name_fr: 'Rhumatologie', name_ar: 'أمراض الروماتيزم' },
  { id: 'physical_medicine', name_fr: 'Médecine physique', name_ar: 'الطب الفيزيائي' },
  { id: 'nephrology', name_fr: 'Néphrologie', name_ar: 'أمراض الكلى' },
  { id: 'neurosurgery', name_fr: 'Neurochirurgie', name_ar: 'جراحة الأعصاب' },
];

const WILAYAS = [
  { code: '01', name_fr: 'Adrar' }, { code: '02', name_fr: 'Chlef' }, { code: '03', name_fr: 'Laghouat' },
  { code: '04', name_fr: 'Oum El Bouaghi' }, { code: '05', name_fr: 'Batna' }, { code: '06', name_fr: 'Béjaïa' },
  { code: '07', name_fr: 'Biskra' }, { code: '08', name_fr: 'Béchar' }, { code: '09', name_fr: 'Blida' },
  { code: '10', name_fr: 'Bouira' }, { code: '11', name_fr: 'Tamanrasset' }, { code: '12', name_fr: 'Tébessa' },
  { code: '13', name_fr: 'Tlemcen' }, { code: '14', name_fr: 'Tiaret' }, { code: '15', name_fr: 'Tizi Ouzou' },
  { code: '16', name_fr: 'Alger' }, { code: '17', name_fr: 'Djelfa' }, { code: '18', name_fr: 'Jijel' },
  { code: '19', name_fr: 'Sétif' }, { code: '20', name_fr: 'Saïda' }, { code: '21', name_fr: 'Skikda' },
  { code: '22', name_fr: 'Sidi Bel Abbès' }, { code: '23', name_fr: 'Annaba' }, { code: '24', name_fr: 'Guelma' },
  { code: '25', name_fr: 'Constantine' }, { code: '26', name_fr: 'Médéa' }, { code: '27', name_fr: 'Mostaganem' },
  { code: '28', name_fr: "M'Sila" }, { code: '29', name_fr: 'Mascara' }, { code: '30', name_fr: 'Ouargla' },
  { code: '31', name_fr: 'Oran' }, { code: '32', name_fr: 'El Bayadh' }, { code: '33', name_fr: 'Illizi' },
  { code: '34', name_fr: 'Bordj Bou Arréridj' }, { code: '35', name_fr: 'Boumerdès' }, { code: '36', name_fr: 'El Tarf' },
  { code: '37', name_fr: 'Tindouf' }, { code: '38', name_fr: 'Tissemsilt' }, { code: '39', name_fr: 'El Oued' },
  { code: '40', name_fr: 'Khenchela' }, { code: '41', name_fr: 'Souk Ahras' }, { code: '42', name_fr: 'Tipaza' },
  { code: '43', name_fr: 'Mila' }, { code: '44', name_fr: 'Aïn Defla' }, { code: '45', name_fr: 'Naâma' },
  { code: '46', name_fr: 'Aïn Témouchent' }, { code: '47', name_fr: 'Ghardaïa' }, { code: '48', name_fr: 'Relizane' },
];

const CHART_COLORS = ['#0596DE', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#D946EF', '#0EA5E9', '#22C55E', '#EAB308'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  // Suivi d'usage : qui se sert reellement de son espace. Charge a part de
  // fetchData volontairement — si l'endpoint n'est pas encore deploye cote
  // serveur, seul cet onglet reste vide, le reste du panneau fonctionne.
  const [activity, setActivity] = useState(null);
  const [activityError, setActivityError] = useState(false);
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [users, setUsers] = useState([]);
  const [claimRequests, setClaimRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [addDoctorDialogOpen, setAddDoctorDialogOpen] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    name: '',
    phone: '',
    specialty: '',
    wilaya_code: '',
    address: '',
    fees: '',
  });

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [filterWilaya, setFilterWilaya] = useState('all');
  const [filterDaira, setFilterDaira] = useState('all');
  const [dairas, setDairas] = useState([]);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);

  // Edit user dialog state (correction du nom/telephone d'un patient,
  // ex: compte cree avec un nom invalide type "/1")
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [adminPage, setAdminPage] = useState(1);
  const [adminTotalPages, setAdminTotalPages] = useState(1);

  // Support (cahier des charges labo, section 37) : tickets tous
  // etablissements confondus, cote equipe Tabbibak.
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [openTicketId, setOpenTicketId] = useState(null);
  const [ticketReplyDraft, setTicketReplyDraft] = useState('');
  const [ticketActionLoading, setTicketActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    fetchActivity();
  }, []);
  useEffect(() => {
    fetchTickets();
  }, [ticketStatusFilter]);

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const params = ticketStatusFilter === 'all' ? {} : { status: ticketStatusFilter };
      const res = await axios.get(`${API_URL}/api/admin/support/tickets`, { ...getAuthConfig(), params });
      setTickets(res.data.tickets || []);
    } catch (error) {
      toast.error('Erreur de chargement des tickets');
    } finally {
      setTicketsLoading(false);
    }
  };

  const viewTicketAttachment = async (ticket) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/support/tickets/${ticket.ticket_id}/attachment`, getAuthConfig());
      window.open(res.data.url, '_blank');
    } catch (error) {
      toast.error("Impossible d'ouvrir la piece jointe");
    }
  };

  const updateTicket = async (ticketId, { body, status }) => {
    setTicketActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/support/tickets/${ticketId}/reply`, { body, status }, getAuthConfig());
      toast.success('Ticket mis a jour');
      setTicketReplyDraft('');
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setTicketActionLoading(false);
    }
  };

  const TICKET_STATUS_LABELS = { ouvert: 'Ouvert', en_cours: 'En cours', resolu: 'Resolu', ferme: 'Ferme' };
  const TICKET_STATUS_COLORS = { ouvert: 'bg-amber-100 text-amber-800', en_cours: 'bg-blue-100 text-blue-800', resolu: 'bg-green-100 text-green-800', ferme: 'bg-slate-100 text-slate-600' };
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 400);
    return () => clearTimeout(t);
  }, [adminPage, searchQuery]);

  // Load dairas when wilaya filter changes
  useEffect(() => {
    if (filterWilaya && filterWilaya !== 'all') {
      axios.get(`${API_URL}/api/wilayas/${filterWilaya}/dairas`)
        .then((res) => setDairas(res.data || []))
        .catch(() => setDairas([]));
    } else {
      setDairas([]);
    }
    setFilterDaira('all');
  }, [filterWilaya]);

  const getAuthConfig = () => {
    // Correctif B7 : le cookie httpOnly porte la session.
    return {
      withCredentials: true,
      headers: {},
    };
  };

  const fetchData = async () => {
    try {
      const config = getAuthConfig();

      const [statsRes, pendingRes, doctorsRes, usersRes, claimsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, config),
        axios.get(`${API_URL}/api/admin/pending-doctors`, config),
        axios.get(`${API_URL}/api/admin/doctors?page=${adminPage}&limit=50&search=${encodeURIComponent(searchQuery)}`, config),
        axios.get(`${API_URL}/api/admin/users`, config),
        axios.get(`${API_URL}/api/admin/claim-requests?status=pending`, config),
      ]);

      setStats(statsRes.data);
      setPendingDoctors(pendingRes.data);
      setAllDoctors(doctorsRes.data.doctors || doctorsRes.data);
      setAdminTotalPages(doctorsRes.data.pages || 1);
      setUsers(usersRes.data);
      setClaimRequests(claimsRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/doctors-activity`, getAuthConfig());
      setActivity(res.data);
      setActivityError(false);
    } catch (error) {
      // Aucun toast : cet onglet est secondaire, il ne doit pas polluer
      // l'ecran principal si le serveur n'est pas a jour.
      console.error('doctors-activity:', error);
      setActivityError(true);
    }
  };

  const dateCourte = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    } catch (e) {
      return '—';
    }
  };

  const handleApprove = async (doctorId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/doctors/approve/${doctorId}`,
        {},
        getAuthConfig()
      );
      toast.success('OK');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateSub = async (doctorId) => {
    if (!window.confirm("Activer / prolonger l'abonnement de ce medecin pour 30 jours ?")) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/subscription/confirm-virement`, { doctor_id: doctorId }, getAuthConfig());
      toast.success(res.data?.message || "Abonnement active");
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Erreur activation abonnement");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (doctorId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/doctors/reject/${doctorId}`,
        {},
        getAuthConfig()
      );
      toast.success('OK');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (doctorId) => {
    if (!window.confirm(t('admin_confirm_delete'))) return;

    setActionLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/doctors/${doctorId}`,
        getAuthConfig()
      );
      toast.success('OK');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error');
    } finally {
      setActionLoading(false);
    }
  };

  // Ajout 20/09/2026 : masquer une fiche sans la supprimer. Utile quand un
  // praticien conteste une fiche importee : elle disparait du site (listes,
  // recherche, lien direct, prise de RDV) mais son origine - source et date
  // d'import - reste conservee en base.
  const handleToggleVisibility = async (doctorId, currentlyHidden) => {
    if (!window.confirm(currentlyHidden ? t('admin_confirm_unhide') : t('admin_confirm_hide'))) return;

    setActionLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/doctors/${doctorId}/visibility`,
        {},
        getAuthConfig()
      );
      toast.success('OK');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleBooking = async (doctorId, currentlyEnabled) => {
    const action = currentlyEnabled ? 'bloquer' : 'débloquer';
    if (!window.confirm(`Voulez-vous ${action} la prise de RDV en ligne pour ce médecin ?`)) return;

    setActionLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/doctors/${doctorId}/booking`,
        {},
        getAuthConfig()
      );
      toast.success('OK');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEdit = (doctor) => {
    setEditingDoctor({
      doctor_id: doctor.doctor_id,
      name: doctor.name || '',
      phone: doctor.phone || '',
      specialty: doctor.specialty || '',
      wilaya_code: doctor.wilaya_code || '',
      address: doctor.address || '',
      fees: doctor.fees != null ? String(doctor.fees) : '',
      photo_url: doctor.photo_url || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingDoctor) return;

    setActionLoading(true);
    try {
      const payload = {
        name: editingDoctor.name,
        phone: editingDoctor.phone,
        specialty: editingDoctor.specialty,
        wilaya_code: editingDoctor.wilaya_code,
        address: editingDoctor.address,
        fees: editingDoctor.fees ? parseInt(editingDoctor.fees, 10) : null,
        photo_url: editingDoctor.photo_url || null,
      };
      await axios.put(
        `${API_URL}/api/admin/doctors/${editingDoctor.doctor_id}`,
        payload,
        getAuthConfig()
      );
      toast.success('Médecin modifié');
      setEditDialogOpen(false);
      setEditingDoctor(null);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la modification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEditUser = (u) => {
    setEditingUser({
      user_id: u.user_id,
      name: u.name || '',
      phone: u.phone || '',
    });
    setEditUserDialogOpen(true);
  };

  const handleSaveEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    setActionLoading(true);
    try {
      // Correctif : le telephone n'est pas toujours connu sur un vieux
      // compte (ex: "/1") - ne l'envoyer que s'il est rempli, sinon le
      // controle "obligatoire" cote serveur bloquait meme la correction du
      // nom seul.
      const payload = { name: editingUser.name };
      if (editingUser.phone && editingUser.phone.trim()) {
        payload.phone = editingUser.phone;
      }
      await axios.put(
        `${API_URL}/api/admin/users/${editingUser.user_id}`,
        payload,
        getAuthConfig()
      );
      toast.success('Utilisateur modifié');
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveClaim = async (requestId) => {
    if (!window.confirm('Approuver cette demande ? Un email d\'activation sera envoyé au médecin.')) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/claim-requests/${requestId}/approve`,
        {},
        getAuthConfig()
      );
      toast.success('Demande approuvée, email envoyé');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };
  const handleRejectClaim = async (requestId) => {
    const reason = window.prompt('Raison du rejet (optionnel) :') || '';
    if (reason === null) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/claim-requests/${requestId}/reject`,
        { reason },
        getAuthConfig()
      );
      toast.success('Demande rejetée');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/admin/doctors/listed`,
        {
          ...newDoctor,
          fees: newDoctor.fees ? parseInt(newDoctor.fees, 10) : null,
        },
        getAuthConfig()
      );

      toast.success('OK');
      setAddDoctorDialogOpen(false);
      setNewDoctor({
        name: '',
        phone: '',
        specialty: '',
        wilaya_code: '',
        address: '',
        fees: '',
      });
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error');
    } finally {
      setActionLoading(false);
    }
  };

  const getSpecName = (id) => {
    const s = SPECIALTIES.find((x) => x.id === id);
    return s ? (lang === 'ar' ? s.name_ar : s.name_fr) : id;
  };

  const getWilName = (code) => WILAYAS.find((w) => w.code === code)?.name_fr || code;

  // Filtered doctors based on search + filters (côté client, instantané)
  const filteredDoctors = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allDoctors.filter((d) => {
      if (filterSpecialty !== 'all' && d.specialty !== filterSpecialty) return false;
      if (filterWilaya !== 'all' && d.wilaya_code !== filterWilaya && d.wilaya !== WILAYAS.find(w => w.code === filterWilaya)?.name_fr) return false;
      if (filterDaira !== 'all') {
        const doctorDaira = (d.daira || d.commune || '').toLowerCase();
        if (doctorDaira !== filterDaira.toLowerCase()) return false;
      }
      if (q) {
        const name = (d.name || '').toLowerCase();
        const phone = (d.phone || '').toLowerCase();
        const address = (d.address || '').toLowerCase();
        if (!name.includes(q) && !phone.includes(q) && !address.includes(q)) return false;
      }
      return true;
    });
  }, [allDoctors, searchQuery, filterSpecialty, filterWilaya, filterDaira]);

  const patientUsers = useMemo(() => users.filter((u) => u.role === 'patient'), [users]);
  const doctorAccountUsers = useMemo(() => users.filter((u) => u.role === 'doctor'), [users]);

  const specialtyChartData = SPECIALTIES.map((s) => ({
    name: lang === 'ar' ? s.name_ar : s.name_fr,
    count: allDoctors.filter((d) => d.specialty === s.id).length,
  }))
    .filter((d) => d.count > 0)
    .sort((a, b) => b.count - a.count);

  const statusPieData = [
    { name: t('admin_approved'), value: allDoctors.filter((d) => d.status === 'approved').length, color: '#10B981' },
    { name: t('admin_pending_status'), value: allDoctors.filter((d) => d.status === 'pending').length, color: '#F59E0B' },
    { name: t('admin_listed_status'), value: allDoctors.filter((d) => d.status === 'listed').length, color: '#0596DE' },
    { name: t('admin_rejected'), value: allDoctors.filter((d) => d.status === 'rejected').length, color: '#EF4444' },
  ].filter((d) => d.value > 0);

  const rolePieData = [
    { name: t('admin_patients'), value: users.filter((u) => u.role === 'patient').length, color: '#0596DE' },
    { name: t('role_doctor'), value: users.filter((u) => u.role === 'doctor').length, color: '#10B981' },
    { name: t('role_admin'), value: users.filter((u) => u.role === 'admin').length, color: '#8B5CF6' },
  ].filter((d) => d.value > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="grid md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 h-32" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-dashboard">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-['Outfit']">
              {t('admin_title')}
            </h1>
            <p className="text-[#64748B] mt-1">{t('admin_subtitle')}</p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/clinics')}
              className="rounded-full"
              data-testid="admin-goto-clinics-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Cliniques
            </Button>
            <Button
              onClick={() => setAddDoctorDialogOpen(true)}
              className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]"
              data-testid="add-doctor-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('admin_add_doctor')}
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {[
            { icon: Users, color: '#0596DE', bg: '#E6F4FB', label: t('admin_patients'), value: stats?.total_patients || 0 },
            { icon: UserCheck, color: '#10B981', bg: '#D1FAE5', label: t('admin_registered'), value: stats?.registered_doctors || 0 },
            { icon: Phone, color: '#475569', bg: '#F1F5F9', label: t('admin_listed'), value: stats?.listed_doctors || 0 },
            { icon: Clock, color: '#F59E0B', bg: '#FEF3C7', label: t('admin_pending'), value: stats?.pending_doctors || 0 },
          ].map(({ icon: Icon, color, bg, label, value }, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: bg }}
                >
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">{label}</p>
                  <p className="text-2xl font-bold text-[#0F172A]">{value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-white rounded-full p-1 border border-[#E2E8F0] flex-wrap">
            <TabsTrigger value="pending" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('admin_tab_pending')} ({pendingDoctors.length})
            </TabsTrigger>
            <TabsTrigger value="claims" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              Revendications ({claimRequests.length})
            </TabsTrigger>
            <TabsTrigger value="doctors" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('admin_tab_doctors')}
            </TabsTrigger>
            <TabsTrigger value="patient_users" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('admin_tab_patient_users')} ({patientUsers.length})
            </TabsTrigger>
            <TabsTrigger value="doctor_users" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('admin_tab_doctor_users')} ({doctorAccountUsers.length})
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-1" />
              Activité
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-1" />
              {t('admin_tab_analytics')}
            </TabsTrigger>
            <TabsTrigger value="support" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              <LifeBuoy className="w-4 h-4 mr-1" />
              Support ({tickets.filter((tk) => tk.status === 'ouvert').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
              {pendingDoctors.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <UserCheck className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                  <p>{t('admin_no_pending')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin_doctor')}</TableHead>
                      <TableHead>{t('admin_specialty')}</TableHead>
                      <TableHead>{t('admin_wilaya')}</TableHead>
                      <TableHead>{t('admin_license')}</TableHead>
                      <TableHead>{t('admin_actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingDoctors.map((doctor) => (
                      <TableRow key={doctor.doctor_id}>
                        <TableCell>
                          <p className="font-medium">{doctor.name}</p>
                          <p className="text-sm text-[#64748B]">{doctor.phone}</p>
                        </TableCell>
                        <TableCell>{getSpecName(doctor.specialty)}</TableCell>
                        <TableCell>{getWilName(doctor.wilaya_code)}</TableCell>
                        <TableCell>{doctor.license_number}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(doctor.doctor_id)}
                              disabled={actionLoading}
                              className="bg-[#10B981] hover:bg-[#059669]"
                              data-testid={`approve-btn-${doctor.doctor_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(doctor.doctor_id)}
                              disabled={actionLoading}
                              data-testid={`reject-btn-${doctor.doctor_id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
          <TabsContent value="claims">
            <div className="space-y-4">
              {claimRequests.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 text-center text-[#64748B]">
                  Aucune demande de revendication en attente.
                </div>
              ) : (
                claimRequests.map((req) => (
                  <div key={req.request_id} className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-[#0F172A]">{formatDoctorName(req.doctor_name)}</h3>
                        <p className="text-sm text-[#64748B]">{req.doctor_current_specialty} — {req.doctor_current_wilaya}</p>
                      </div>
                      <span className="text-xs text-[#64748B]">{new Date(req.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                      <div className="bg-[#F8FAFC] rounded-lg p-3">
                        <div className="text-xs font-semibold text-[#64748B] uppercase mb-1">Fiche actuelle</div>
                        <div className="text-[#0F172A]">Tél : {req.doctor_current_phone || '—'}</div>
                      </div>
                      <div className="bg-[#EFF6FF] rounded-lg p-3">
                        <div className="text-xs font-semibold text-[#0596DE] uppercase mb-1">Infos soumises</div>
                        <div className="text-[#0F172A]">Email : {req.submitted_email}</div>
                        <div className="text-[#0F172A]">Tél : {req.submitted_phone}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveClaim(req.request_id)}
                        disabled={actionLoading}
                        className="bg-[#10B981] hover:bg-[#059669] text-white"
                      >
                        Approuver
                      </Button>
                      <Button
                        onClick={() => handleRejectClaim(req.request_id)}
                        disabled={actionLoading}
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        Rejeter
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="doctors">
            <div className="space-y-4">
              {/* Search and filter bar */}
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input
                      placeholder="Rechercher par nom, téléphone ou adresse..."
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setAdminPage(1); }}
                      className="pl-9"
                      data-testid="admin-search-input"
                    />
                  </div>
                  <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                    <SelectTrigger className="md:w-56" data-testid="admin-filter-specialty">
                      <SelectValue placeholder="Spécialité" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les spécialités</SelectItem>
                      {SPECIALTIES.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {lang === 'ar' ? s.name_ar : s.name_fr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterWilaya} onValueChange={setFilterWilaya}>
                    <SelectTrigger className="md:w-48" data-testid="admin-filter-wilaya">
                      <SelectValue placeholder="Wilaya" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les wilayas</SelectItem>
                      {WILAYAS.map((w) => (
                        <SelectItem key={w.code} value={w.code}>
                          {w.code} - {w.name_fr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterDaira} onValueChange={setFilterDaira} disabled={filterWilaya === 'all'}>
                    <SelectTrigger className="md:w-48" data-testid="admin-filter-daira">
                      <SelectValue placeholder="Daïra" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les daïras</SelectItem>
                      {dairas.map((d) => (
                        <SelectItem key={d.name_fr} value={d.name_fr}>
                          {lang === 'ar' ? d.name_ar : d.name_fr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-3 text-sm text-[#64748B]">
                  {filteredDoctors.length} résultat(s) sur {allDoctors.length} médecins
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin_doctor')}</TableHead>
                      <TableHead>{t('admin_specialty')}</TableHead>
                      <TableHead>{t('admin_wilaya')}</TableHead>
                      <TableHead>{t('admin_type')}</TableHead>
                      <TableHead>{t('admin_status')}</TableHead>
                      <TableHead>{t('admin_actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDoctors.map((doctor) => (
                      <TableRow key={doctor.doctor_id}>
                        <TableCell>
                          <p className="font-medium">{doctor.name}</p>
                          <p className="text-sm text-[#64748B]">{doctor.phone}</p>
                        </TableCell>
                        <TableCell>{getSpecName(doctor.specialty)}</TableCell>
                        <TableCell>{getWilName(doctor.wilaya_code)}</TableCell>
                        <TableCell>
                          {doctor.is_registered ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D1FAE5] text-[#10B981] text-xs font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                              {t('admin_online')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F1F5F9] text-[#475569] text-xs font-medium">
                              <Phone className="w-3 h-3" />
                              {t('admin_phone_type')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              doctor.status === 'approved'
                                ? 'bg-[#D1FAE5] text-[#10B981]'
                                : doctor.status === 'pending'
                                  ? 'bg-[#FEF3C7] text-[#F59E0B]'
                                  : doctor.status === 'listed'
                                    ? 'bg-[#E6F4FB] text-[#0596DE]'
                                    : 'bg-red-100 text-red-600'
                            }`}
                          >
                            {doctor.status === 'approved'
                              ? t('admin_approved')
                              : doctor.status === 'pending'
                                ? t('admin_pending_status')
                                : doctor.status === 'listed'
                                  ? t('admin_listed_status')
                                  : t('admin_rejected')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEdit(doctor)}
                              disabled={actionLoading}
                              className="text-[#0596DE] border-[#BAE6FD] hover:bg-[#E6F4FB]"
                              title="Modifier le médecin"
                              data-testid={`edit-btn-${doctor.doctor_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleBooking(doctor.doctor_id, doctor.online_booking_enabled !== false)}
                              disabled={actionLoading}
                              className={
                                doctor.online_booking_enabled === false
                                  ? "text-orange-600 border-orange-200 hover:bg-orange-50"
                                  : "text-[#10B981] border-[#A7F3D0] hover:bg-[#D1FAE5]"
                              }
                              title={doctor.online_booking_enabled === false ? "Débloquer la prise de RDV en ligne" : "Bloquer la prise de RDV en ligne"}
                              data-testid={`toggle-booking-btn-${doctor.doctor_id}`}
                            >
                              {doctor.online_booking_enabled === false ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleActivateSub(doctor.doctor_id)}
                              disabled={actionLoading}
                              className="text-[#10B981] border-[#A7F3D0] hover:bg-[#D1FAE5]"
                              title="Activer / prolonger l'abonnement (30 jours)"
                              data-testid={`activate-sub-btn-${doctor.doctor_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            {/* Masquer sans supprimer : la fiche sort du site
                                mais reste en base, avec son origine. */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleVisibility(doctor.doctor_id, doctor.hidden === true)}
                              disabled={actionLoading}
                              className={
                                doctor.hidden === true
                                  ? "text-orange-600 border-orange-200 hover:bg-orange-50"
                                  : "text-[#64748B] border-[#E2E8F0] hover:bg-[#F1F5F9]"
                              }
                              title={doctor.hidden === true ? t('admin_unhide_doctor') : t('admin_hide_doctor')}
                              data-testid={`toggle-visibility-btn-${doctor.doctor_id}`}
                            >
                              {doctor.hidden === true ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(doctor.doctor_id)}
                              disabled={actionLoading}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`delete-btn-${doctor.doctor_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {adminTotalPages > 1 && (
  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0]">
    <p className="text-sm text-[#64748B]">Page {adminPage} sur {adminTotalPages}</p>
    <div className="flex gap-2">
      <button onClick={() => setAdminPage(p => Math.max(1, p - 1))} disabled={adminPage === 1} className="px-3 py-1 text-sm border border-[#E2E8F0] rounded-lg disabled:opacity-50 hover:bg-[#F8FAFC]">Précédent</button>
      <button onClick={() => setAdminPage(p => Math.min(adminTotalPages, p + 1))} disabled={adminPage === adminTotalPages} className="px-3 py-1 text-sm border border-[#E2E8F0] rounded-lg disabled:opacity-50 hover:bg-[#F8FAFC]">Suivant</button>
    </div>
  </div>
)}
            </div>
          </TabsContent>

          <TabsContent value="patient_users">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
              {patientUsers.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <Users className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                  <p>{t('admin_no_patient_users')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin_user')}</TableHead>
                      <TableHead>{t('admin_email')}</TableHead>
                      <TableHead>{t('admin_phone')}</TableHead>
                      <TableHead>{t('admin_role')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patientUsers.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.phone || '-'}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#475569]">
                            {t('role_patient')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenEditUser(u)}
                            disabled={actionLoading}
                            className="text-[#0596DE] border-[#BAE6FD] hover:bg-[#E6F4FB]"
                            title="Modifier l'utilisateur"
                            data-testid={`edit-user-btn-${u.user_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="doctor_users">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
              {doctorAccountUsers.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <UserCheck className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                  <p>{t('admin_no_doctor_users')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin_user')}</TableHead>
                      <TableHead>{t('admin_email')}</TableHead>
                      <TableHead>{t('admin_phone')}</TableHead>
                      <TableHead>{t('admin_role')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {doctorAccountUsers.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.phone || '-'}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#E6F4FB] text-[#0596DE]">
                            {t('role_doctor')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenEditUser(u)}
                            disabled={actionLoading}
                            className="text-[#0596DE] border-[#BAE6FD] hover:bg-[#E6F4FB]"
                            title="Modifier l'utilisateur"
                            data-testid={`edit-user-btn-${u.user_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activity" data-testid="activity-tab">
            {activityError ? (
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-10 text-center text-[#64748B]">
                Données d'activité indisponibles pour le moment.
              </div>
            ) : !activity ? (
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-10 text-center text-[#64748B]">
                Chargement…
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: 'Inscrits', value: activity.resume.inscrits, color: '#0F172A' },
                    { label: 'Horaires configurés', value: activity.resume.avec_horaires, color: '#0596DE' },
                    { label: 'Au moins 1 RDV', value: activity.resume.avec_au_moins_un_rdv, color: '#10B981' },
                    { label: 'Actifs sur 30 jours', value: activity.resume.actifs_30j, color: '#059669' },
                    { label: 'Jamais démarrés', value: activity.resume.jamais_demarres, color: '#B45309' },
                  ].map((k) => (
                    <div key={k.label} className="bg-white rounded-2xl border border-[#E2E8F0] p-4">
                      <p className="text-xs text-[#64748B]">{k.label}</p>
                      <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
                    </div>
                  ))}
                </div>

                {stats && (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                    <p className="text-sm font-semibold text-[#0F172A] mb-3">
                      Origine des rendez-vous ({stats.total_appointments} au total)
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="bg-[#F8FAFC] rounded-lg px-4 py-3">
                        <span className="font-bold text-[#0596DE] text-lg">{stats.appointments_online ?? '—'}</span>
                        <span className="text-[#475569]"> réservés en ligne par un patient</span>
                      </div>
                      <div className="bg-[#F8FAFC] rounded-lg px-4 py-3">
                        <span className="font-bold text-[#475569] text-lg">{stats.appointments_by_doctor ?? '—'}</span>
                        <span className="text-[#475569]"> créés par le médecin</span>
                      </div>
                      <div className="bg-[#F8FAFC] rounded-lg px-4 py-3">
                        <span className="font-bold text-[#475569] text-lg">{stats.appointments_by_clinic ?? '—'}</span>
                        <span className="text-[#475569]"> créés par un secrétariat</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8FAFC] text-[#64748B]">
                      <tr>
                        <th className="text-left font-medium px-4 py-3">Médecin</th>
                        <th className="text-left font-medium px-4 py-3">Wilaya</th>
                        <th className="text-center font-medium px-4 py-3">Créneaux ouverts</th>
                        <th className="text-center font-medium px-4 py-3">RDV total</th>
                        <th className="text-center font-medium px-4 py-3">dont en ligne</th>
                        <th className="text-center font-medium px-4 py-3">30 jours</th>
                        <th className="text-center font-medium px-4 py-3">Ordonnances</th>
                        <th className="text-left font-medium px-4 py-3">Dernière réservation</th>
                        <th className="text-left font-medium px-4 py-3">Abonnement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.doctors.map((d) => (
                        <tr key={d.doctor_id} className="border-t border-[#E2E8F0]">
                          <td className="px-4 py-3">
                            <span className="font-medium text-[#0F172A]">{formatDoctorName(d.name)}</span>
                            {!d.horaires_configures && d.rdv_total === 0 && (
                              <span className="ml-2 text-xs bg-[#FEF3C7] text-[#B45309] rounded-full px-2 py-0.5">
                                jamais démarré
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[#475569]">{d.wilaya || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            {d.creneaux_ouverts > 0
                              ? <span className="text-[#0F172A]">{d.creneaux_ouverts}</span>
                              : <span className="text-[#A32D2D]">aucun</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-[#0F172A]">{d.rdv_total}</td>
                          <td className="px-4 py-3 text-center text-[#0596DE] font-medium">{d.rdv_en_ligne}</td>
                          <td className="px-4 py-3 text-center text-[#0F172A]">{d.rdv_30j}</td>
                          <td className="px-4 py-3 text-center text-[#475569]">{d.ordonnances}</td>
                          <td className="px-4 py-3 text-[#475569]">{dateCourte(d.derniere_reservation)}</td>
                          <td className="px-4 py-3 text-[#475569]">{d.abonnement || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-[#64748B]">
                  Un médecin sans créneau ouvert ne peut recevoir aucune réservation, quelle
                  que soit son activité par ailleurs. « dont en ligne » compte les rendez-vous
                  pris par un patient depuis le site, par opposition à ceux saisis par le
                  praticien ou un secrétariat.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" data-testid="analytics-tab">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4">
                  {t('admin_chart_specialty')}
                </h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={specialtyChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {specialtyChartData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-4">
                    {t('admin_chart_overview')}
                  </h3>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusPieData}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                          labelLine={false}
                        >
                          {statusPieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-4">
                    {t('admin_tab_users')}
                  </h3>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={rolePieData}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                          labelLine={false}
                        >
                          {rolePieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="support">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-[#0F172A]">Tickets de support (section 37)</h3>
                <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                  <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="ouvert">Ouvert</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="resolu">Resolu</SelectItem>
                    <SelectItem value="ferme">Ferme</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {ticketsLoading && <div className="text-center py-8 text-[#64748B]">Chargement...</div>}
              {!ticketsLoading && tickets.length === 0 && (
                <div className="text-center py-12 text-[#64748B]">
                  <LifeBuoy className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                  <p>Aucun ticket.</p>
                </div>
              )}
              {!ticketsLoading && tickets.map((tk) => {
                const isOpen = openTicketId === tk.ticket_id;
                return (
                  <div key={tk.ticket_id} className="border border-[#E2E8F0] rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setOpenTicketId(isOpen ? null : tk.ticket_id)}>
                      <div>
                        <div className="font-medium text-[#0F172A]">{tk.clinic_name || tk.clinic_id} · {tk.category} · {tk.priority}</div>
                        <div className="text-sm text-[#64748B] mt-1">{tk.description?.slice(0, 100)}{tk.description?.length > 100 ? '…' : ''}</div>
                        <div className="text-xs text-[#94A3B8] mt-1">{tk.author_name} · {new Date(tk.created_at).toLocaleString('fr-FR')}</div>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${TICKET_STATUS_COLORS[tk.status] || 'bg-slate-100 text-slate-600'}`}>
                        {TICKET_STATUS_LABELS[tk.status] || tk.status}
                      </span>
                    </div>
                    {isOpen && (
                      <div className="mt-4 pt-4 border-t border-[#F1F5F9] space-y-3">
                        <p className="text-sm text-[#334155] whitespace-pre-wrap">{tk.description}</p>
                        {tk.attachment && (
                          <button onClick={() => viewTicketAttachment(tk)} className="text-sm text-[#0596DE] flex items-center gap-1">
                            <Paperclip className="w-3.5 h-3.5" /> {tk.attachment.filename}
                          </button>
                        )}
                        {(tk.replies || []).map((r, i) => (
                          <div key={i} className={`rounded-lg p-3 ${r.from === 'admin' ? 'bg-[#E6F1FB]' : 'bg-[#F8FAFC]'}`}>
                            <div className="text-xs font-semibold text-[#334155]">{r.author} · {new Date(r.created_at).toLocaleString('fr-FR')}</div>
                            <div className="text-sm text-[#334155] whitespace-pre-wrap mt-1">{r.body}</div>
                          </div>
                        ))}
                        <div className="flex flex-wrap items-center gap-2">
                          <Input value={ticketReplyDraft} onChange={(e) => setTicketReplyDraft(e.target.value)} placeholder="Repondre a l'etablissement..." className="flex-1 min-w-[200px]" />
                          <Button size="sm" disabled={ticketActionLoading || !ticketReplyDraft.trim()} onClick={() => updateTicket(tk.ticket_id, { body: ticketReplyDraft.trim() })}>Envoyer</Button>
                          {tk.status !== 'en_cours' && <Button size="sm" variant="outline" disabled={ticketActionLoading} onClick={() => updateTicket(tk.ticket_id, { status: 'en_cours' })}>En cours</Button>}
                          {tk.status !== 'resolu' && <Button size="sm" variant="outline" disabled={ticketActionLoading} onClick={() => updateTicket(tk.ticket_id, { status: 'resolu' })}>Resolu</Button>}
                          {tk.status !== 'ferme' && <Button size="sm" variant="outline" disabled={ticketActionLoading} onClick={() => updateTicket(tk.ticket_id, { status: 'ferme' })}>Fermer</Button>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add doctor dialog */}
      <Dialog open={addDoctorDialogOpen} onOpenChange={setAddDoctorDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin_add_title')}</DialogTitle>
            <DialogDescription>{t('admin_add_desc')}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddDoctor} className="space-y-4" data-testid="add-doctor-form">
            <div>
              <Label>{t('admin_name')}</Label>
              <Input
                value={newDoctor.name}
                onChange={(e) => setNewDoctor({ ...newDoctor, name: e.target.value })}
                placeholder="Dr. Nom Prénom"
                required
                data-testid="new-doctor-name"
              />
            </div>

            <div>
              <Label>{t('admin_phone')}</Label>
              <Input
                value={newDoctor.phone}
                onChange={(e) => setNewDoctor({ ...newDoctor, phone: e.target.value })}
                placeholder="+213 5XX XX XX XX"
                required
                data-testid="new-doctor-phone"
              />
            </div>

            <div>
              <Label>{t('admin_specialty')}</Label>
              <Select
                value={newDoctor.specialty}
                onValueChange={(v) => setNewDoctor({ ...newDoctor, specialty: v })}
              >
                <SelectTrigger data-testid="new-doctor-specialty">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {lang === 'ar' ? s.name_ar : s.name_fr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('admin_wilaya')}</Label>
              <Select
                value={newDoctor.wilaya_code}
                onValueChange={(v) => setNewDoctor({ ...newDoctor, wilaya_code: v })}
              >
                <SelectTrigger data-testid="new-doctor-wilaya">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WILAYAS.map((w) => (
                    <SelectItem key={w.code} value={w.code}>
                      {w.code} - {w.name_fr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('admin_address')}</Label>
              <Input
                value={newDoctor.address}
                onChange={(e) => setNewDoctor({ ...newDoctor, address: e.target.value })}
                required
                data-testid="new-doctor-address"
              />
            </div>

            <div>
              <Label>{t('admin_fees')}</Label>
              <Input
                type="number"
                value={newDoctor.fees}
                onChange={(e) => setNewDoctor({ ...newDoctor, fees: e.target.value })}
                data-testid="new-doctor-fees"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDoctorDialogOpen(false)}>
                {t('admin_cancel')}
              </Button>
              <Button
                type="submit"
                disabled={actionLoading}
                className="bg-[#0596DE]"
                data-testid="submit-new-doctor"
              >
                {actionLoading ? t('admin_submitting') : t('admin_submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit doctor dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le médecin</DialogTitle>
            <DialogDescription>Mettez à jour les informations du médecin</DialogDescription>
          </DialogHeader>

          {editingDoctor && (
            <form onSubmit={handleSaveEdit} className="space-y-4" data-testid="edit-doctor-form">
              <div>
                <Label>Photo</Label>
                <ImageUploader
                  value={editingDoctor.photo_url}
                  onChange={(url) => setEditingDoctor({ ...editingDoctor, photo_url: url })}
                  label="Envoyer une photo"
                  aspect="square"
                  className="w-32"
                />
              </div>

              <div>
                <Label>Nom</Label>
                <Input
                  value={editingDoctor.name}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, name: e.target.value })}
                  required
                  data-testid="edit-doctor-name"
                />
              </div>

              <div>
                <Label>Téléphone</Label>
                <Input
                  value={editingDoctor.phone}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, phone: e.target.value })}
                  required
                  data-testid="edit-doctor-phone"
                />
              </div>

              <div>
                <Label>Spécialité</Label>
                <Select
                  value={editingDoctor.specialty}
                  onValueChange={(v) => setEditingDoctor({ ...editingDoctor, specialty: v })}
                >
                  <SelectTrigger data-testid="edit-doctor-specialty">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {lang === 'ar' ? s.name_ar : s.name_fr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Wilaya</Label>
                <Select
                  value={editingDoctor.wilaya_code}
                  onValueChange={(v) => setEditingDoctor({ ...editingDoctor, wilaya_code: v })}
                >
                  <SelectTrigger data-testid="edit-doctor-wilaya">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WILAYAS.map((w) => (
                      <SelectItem key={w.code} value={w.code}>
                        {w.code} - {w.name_fr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Adresse</Label>
                <Input
                  value={editingDoctor.address}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, address: e.target.value })}
                  data-testid="edit-doctor-address"
                />
              </div>

              <div>
                <Label>Honoraires (DA)</Label>
                <Input
                  type="number"
                  value={editingDoctor.fees}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, fees: e.target.value })}
                  data-testid="edit-doctor-fees"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-[#0596DE]"
                  data-testid="submit-edit-doctor"
                >
                  {actionLoading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>Corrigez le nom ou le téléphone de ce compte</DialogDescription>
          </DialogHeader>

          {editingUser && (
            <form onSubmit={handleSaveEditUser} className="space-y-4" data-testid="edit-user-form">
              <div>
                <Label>Nom</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  required
                  data-testid="edit-user-name"
                />
              </div>

              <div>
                <Label>Téléphone</Label>
                <Input
                  value={editingUser.phone}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  data-testid="edit-user-phone"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-[#0596DE]"
                  data-testid="submit-edit-user"
                >
                  {actionLoading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
